import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { faBriefcase, faEnvelope, faLocationDot, faLock, faPhone, faRocket } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState } from 'react';
import Swal from 'sweetalert2';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const showSuccessAlert = () => {
    Swal.fire({
      title: 'Success!',
      text: 'Your message has been sent successfully! We will get back to you within 2 hours.',
      icon: 'success',
      confirmButtonText: 'OK',
      confirmButtonColor: '#2563eb',
      timer: 5000,
      timerProgressBar: true,
    });
  };

  const showErrorAlert = () => {
    Swal.fire({
      title: 'Error!',
      text: 'Failed to send your message. Please try again later.',
      icon: 'error',
      confirmButtonText: 'OK',
      confirmButtonColor: '#dc2626'
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Since we're using FormSubmit.co, we'll let it handle the submission
      // But we'll prevent the default form submission to show our custom alert first
      
      // Create a temporary form to submit to FormSubmit.co
      const form = e.target;
      const formData = new FormData(form);
      
      // Submit to FormSubmit.co
      const response = await fetch(form.action, {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        setIsSubmitted(true);
        showSuccessAlert();
        
        // Reset form after successful submission
        setTimeout(() => {
          setIsSubmitted(false);
          setFormData({
            name: '',
            email: '',
            phone: '',
            service: '',
            message: ''
          });
        }, 3000);
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      showErrorAlert();
    } finally {
      setIsLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: faPhone,
      title: 'Call Us',
      details: ' 0191 250 8966',
      description: 'Mon-Fri: 9AM-6PM | Sat-Sun: Closed',
      link:"tel:01912508966"
    },
    {
      icon: faEnvelope,
      title: 'Email Us',
      details: 'globalconsultancy3877@gmail.com',
      description: 'We respond within 2 hours',
      link:"mailto:globalconsultancy3877@gmail.com"
    },
    {
      icon: faLocationDot,
      title: 'Visit Us',
      details: '19 Fenham Road Newcastle Upon Tyne NE4 5PB',
      description: 'Book an appointment in advance',
      link:'https://www.google.com/maps/search/?api=1&query=19+Fenham+Road+Newcastle+Upon+Tyne+NE4+5PB'
    },
    {
      icon: faWhatsapp,
      title: 'WhatsApp',
      details: '07796 592 487',
      link:'https://wa.me/447796592487'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header Section */}
      <section className="pt-24 pb-16 bg-gradient-to-r from-blue-600 to-cyan-600 text-white" >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-aos="zoom-in">
          <h1 className="text-4xl font-bold mb-4">Contact Us</h1>
          <p className="text-xl text-blue-100">
            Get in touch with our UK immigration experts
          </p>
        </div>
      </section>

      {/* Main Content - Contact Info & Form */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            
            {/* Left Side - Contact Information */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8" data-aos='fade-down'>Get In Touch</h2>
              
              <div className="space-y-6 mb-8">
                {contactInfo.map((contact, index) => (
                  <div key={index} className="flex items-start space-x-4" data-aos="fade-right">
                    <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 text-xl">
                      <FontAwesomeIcon icon={contact.icon}/>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 text-lg">{contact.title}</h3>
                      <a href={contact.link}>
                      <p className="text-blue-600 font-medium hover:scale-110 hover:ms-5">{contact.details}</p>
                      </a>
                      <p className="text-gray-600 text-sm">{contact.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Office Hours */}
              <div className="bg-blue-50 rounded-xl p-6 border border-blue-200" data-aos='zoom-in'>
                <h3 className="font-bold text-gray-900 mb-3">Office Hours</h3>
                <div className="space-y-2 text-sm text-gray-700">
                  <div className="flex justify-between">
                    <span>Monday - Friday:</span>
                    <span className="font-semibold">9:00 AM - 6:00 PM</span>
                  </div>
               
                  <div className="flex justify-between">
                    <span>Saturday - Sunday:</span>
                    <span className="font-semibold">Closed</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side - Contact Form */}
            <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm" data-aos='fade-left'>
              <h2 className="text-2xl font-bold text-gray-900 mb-6" >Send us a message</h2>
              
              {isSubmitted ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl text-green-600">✓</span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Thank You!</h3>
                  <p className="text-gray-600">
                    Your message has been sent. We'll get back to you within 2 hours.
                  </p>
                </div>
              ) : (
                <form 
                  action="https://formsubmit.co/globalconsultancy3877@gmail.com" 
                  method="POST"
                  onSubmit={handleSubmit}
                  className="space-y-6"
                >
                  {/* FormSubmit.co configuration */}
                  <input type="hidden" name="_subject" value="New Contact Form Submission - Unique Visa Services" />
                  <input type="hidden" name="_template" value="table" />
                  <input type="hidden" name="_captcha" value="false" />
                  <input type="hidden" name="_autoresponse" value="Thank you for contacting Unique Visa Services! We have received your message and will get back to you within 2 hours. For urgent inquiries, please call us at +44 (0) 123 456 7890." />
                  <input type="hidden" name="_next" value={window.location.origin} />
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-300"
                      placeholder="Your full name"
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-300"
                        placeholder="your@email.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-300"
                        placeholder="+44 (0) 123 456 7890"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Service Interested In</label>
                    <select
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-300"
                    >
                      <option value="">Select a service</option>
                      <option value="Student Visa">Student Visa</option>
                      <option value="Work Visa">Work Visa</option>
                      <option value="Family Visa">Family Visa</option>
                      <option value="Business Visa">Business Visa</option>
                      <option value="Visit Visa">Visit Visa</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Your Message *</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows="4"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-300"
                      placeholder="Tell us about your immigration needs..."
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-blue-600 text-white py-4 rounded-lg font-semibold hover:bg-blue-700 disabled:bg-blue-400 transition duration-300 flex items-center justify-center"
                  >
                    {isLoading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Sending...
                      </>
                    ) : (
                      'Send Message'
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

       {/* Google Map */}
      <section className="w-full">
      <div className="w-full h-[450px] mt-10">
        <iframe
          title="map"
          className="w-full h-full border-0"
          loading="lazy"
          allowFullScreen
          referrerPolicy="no-referrer-when-downgrade"
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2277.7326549518576!2d-1.640192!3d54.977515!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x487e773957788293%3A0x3e81034ed611c24!2s19%20Fenham%20Rd%2C%20Newcastle%20upon%20Tyne%20NE4%205PB%2C%20UK!5e0!3m2!1sen!2suk!4v1700000000000"
        />
      </div>
      </section>

      {/* Quick Info Bar */}
      <section className="bg-gray-900 text-white py-8" data-aos='zoom-in'>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-2xl mb-2"><FontAwesomeIcon icon={faRocket}/></div>
              <p className="font-semibold">Fast Response</p>
              <p className="text-gray-400 text-sm">Within 2 hours</p>
            </div>
            <div>
              <div className="text-2xl mb-2"><FontAwesomeIcon icon={faBriefcase}/></div>
              <p className="font-semibold">Expert Team</p>
              <p className="text-gray-400 text-sm">Complete Expert Team </p>
            </div>
            <div>
              <div className="text-2xl mb-2"><FontAwesomeIcon icon={faLock}/></div>
              <p className="font-semibold">Confidential</p>
              <p className="text-gray-400 text-sm">Secure & private</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;